'use client';
import React, { useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Edit, Trash2, Eye, EyeOff } from 'lucide-react';

interface HarmonicGroupMenuProps {
  open: boolean;
  onClose: () => void;
  onEdit: () => void;
  onRemove: () => void;
  onFocus: () => void;
  position?: { x: number; y: number };
  isFocused?: boolean;
  showEdit?: boolean; // Whether to show the Edit button (default: true)
}

/**
 * Menu that appears when clicking on a harmonic starting point
 * Provides Edit, Remove, and Focus options
 */
export const HarmonicGroupMenu: React.FC<HarmonicGroupMenuProps> = ({
  open,
  onClose,
  onEdit,
  onRemove,
  onFocus,
  position,
  isFocused = false,
  showEdit = true,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  // Click outside handler to close menu
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    // Add delay to prevent immediate closing from the same click that opened it
    setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      ref={menuRef}
      style={{
        position: 'fixed',
        left: position?.x || 0,
        top: position?.y || 0,
        zIndex: 9999,
      }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-2 min-w-[180px]">
        <div className="flex flex-col gap-0.5">
          {showEdit && (
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start gap-2 hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => {
                onEdit();
                onClose();
              }}
            >
              <Edit className="w-4 h-4" />
              Edit
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start gap-2 hover:bg-gray-100 dark:hover:bg-gray-700"
            onClick={() => {
              onFocus();
              onClose();
            }}
          >
            {isFocused ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            {isFocused ? 'Unfocus' : 'Focus'}
          </Button>
          <div className="border-t border-gray-200 dark:border-gray-600 my-1" />
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start gap-2 hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400"
            onClick={() => {
              onRemove();
              onClose();
            }}
          >
            <Trash2 className="w-4 h-4" />
            Remove
          </Button>
        </div>
      </div>
    </div>
  );
};
